import type { Configuration } from '../../../../../types/Configuration';
import type {
  ECommerceEventData,
  EcommerceItemWithDetails,
  EcommerceItem,
} from '../../../../../types/analytics/ECommerce';
import type { Experience, Order, BidPurchaseOption } from '../../../../../types/CoreShoppingExperience';
import convertToECommerceEvent from './convertToECommerceEvent';
import convertToProductDetails from './convertToProductDetails';
import UpgradeType from '../../../../../types/airline/UpgradeType';
import isAutoCampaign from '../../campaign/isAutoCampaign';
import type { RealTimeBiddingData } from '../../../../../types/analytics/GoogleAnalytics';
import { hasContinuousPricingFromPurchaseOption } from './hasContinuousPricing';

const convertToModifyEvent = (
  requestId: string,
  order: Order,
  purchaseOption: BidPurchaseOption,
  experience: Experience,
  partnerConfig: Configuration,
  realTimeBiddingData: RealTimeBiddingData | undefined,
): ECommerceEventData | undefined => {
  const steps = experience.purchasableTrips.flatMap((x) => x.steps);
  const products = steps.flatMap((x) => x.products);
  const product = products.find((product) =>
    product.variants.some((variant) => variant.encryptedId === order.encryptedVariantId),
  );

  if (!product) {
    return;
  }

  const { paymentOption, upgradeType, textKey } = order;
  const { productDetails, variants } = product;
  const { fromCabin } = variants[0];
  const { campaign, paymentBreakdown, quantity, purchaseMode } = purchaseOption;

  const item: EcommerceItem = {
    index: undefined,
    itemName: `${upgradeType} - ${purchaseMode} - ${paymentOption}`,
    itemBrand: textKey,
    price: paymentBreakdown.amount,
    tax: paymentBreakdown.taxes.reduce((acc, iter) => acc + iter.amount, 0),
    fee: paymentBreakdown.fees.reduce((acc, iter) => acc + iter.amount, 0),
    quantity: quantity.quantity,
    upgradeType,
    fromCabin,
    purchaseMode,
    paymentOptions: [paymentOption],
    purchaseOptionSettings: undefined,
    hasSeatMapOption: upgradeType === UpgradeType.EXTRA_SEAT,
    seats: [],
    blockedSeats: [],
    discount: 0,
    autoCampaign: isAutoCampaign(campaign),
    autoCampaignDiscount: isAutoCampaign(campaign) ? (campaign?.percentReduction ?? 0) : 0,
    realTimeBiddingData,
    continuousPricing: hasContinuousPricingFromPurchaseOption(purchaseOption),
  };

  const items: EcommerceItemWithDetails[] = [
    {
      ...item,
      ...convertToProductDetails(productDetails),
    },
  ];

  return convertToECommerceEvent(requestId, items, partnerConfig);
};

export default convertToModifyEvent;
